import { swrOptions } from 'apis/ApiBase';
import useSwr, { SWRConfiguration, mutate } from 'swr';

export default function useSWR<T>(url?: string, fallbackData?: T, options?: SWRConfiguration) {
  return useSwr<T>(url ? [url] : undefined, {
    ...swrOptions,
    ...options,
    fallbackData: fallbackData,
  });
}

const isMatchingKey = (match: string) => (key: string | string[]) => {
  if (Array.isArray(key)) {
    return key[0] === match;
  }
  return key === match;
};

export function invalidate<T>(key?: string, payload?: T) {
  if (!key) {
    return;
  }
  if (payload) {
    // Repopulate directly with the provided payload
    mutate(isMatchingKey(key), payload, { revalidate: false });
  } else {
    // Refetches the data in the background and repopulates without ceremony
    mutate(isMatchingKey(key));
  }
}

// use this to invalidate all keys that start with a certain string
// e.g. resources that have different query params
export const invalidateStartsWith = (keys: string[]) => {
  keys.forEach(_key => {
    mutate((key: any) => {
      const isArray = Array.isArray(key);
      const isString = typeof key === 'string';
      const hasStringKey = isArray && typeof key[0] === 'string';

      let keyToCheck: string | null = null;
      if (hasStringKey) {
        keyToCheck = key[0];
      } else if (isString) {
        keyToCheck = key;
      }

      return keyToCheck && keyToCheck.startsWith(_key);
    });
  });
};

// Helper function to escape special regex characters
const escapeRegex = (string: string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

export const invalidateWithReplacement = (keys: string[], replaceMe: string = '<replace-me>') => {
  keys.forEach(_key => {
    // Create a regex pattern by replacing the placeholder with a pattern that matches any non-slash characters
    const regexPattern = _key.replace(new RegExp(escapeRegex(replaceMe), 'g'), '[^/]+');
    // Add optional query parameters pattern at the end
    const regex = new RegExp(`^${regexPattern}(\\?.*)?$`);

    mutate(key => {
      const isArray = Array.isArray(key);
      const hasStringKey = isArray && typeof key[0] === 'string';
      const regexMatch = hasStringKey ? regex.test(key[0]) : false;
      return isArray && hasStringKey && regexMatch;
    });
  });
};
